import { useState } from 'react';
import type { Identity, WalletExport } from '@cef-ai/wallet-identity';
import { Card, Field, Button, CopyValue, ErrorText } from '~/ui/primitives';
import { downloadTextFile } from '~/wallet/export/download';
import { isEmbedSurface } from '~/wallet/export/isEmbedSurface';

/** Floor for the encryption passphrase. Not a strength meter — see task notes. */
const MIN_PASSPHRASE_LENGTH = 8;

/**
 * Standalone-SPA account export. Renders only when the identity supports
 * `WalletExport` (feature-detected by the parent). Cere + EVM download as
 * password-encrypted keystores; Solana is a separate, confirmed raw reveal
 * (Phantom cannot import an encrypted file).
 *
 * Only the `/settings` route mounts this component, but as a runtime
 * belt-and-suspenders check atop that route separation it also refuses to
 * render at all when `isEmbedSurface()` is true — see
 * `src/wallet/export/isEmbedSurface.ts`.
 */
export function ExportAccount({ identity }: { identity: Identity & WalletExport }) {
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [busy, setBusy] = useState<null | 'cere' | 'evm'>(null);
  const [error, setError] = useState<string | null>(null);
  const [solanaConfirming, setSolanaConfirming] = useState(false);
  const [solanaSecret, setSolanaSecret] = useState<string | null>(null);

  // Runtime belt-and-suspenders atop route separation. Placed AFTER every hook
  // so hook order stays unconditional (Rules of Hooks) regardless of pathname.
  if (isEmbedSurface()) return null;

  const addr = identity.addresses;

  async function download(chain: 'cere' | 'evm') {
    setError(null);
    setBusy(chain);
    try {
      const json = await identity.exportKeystore(chain, passphrase);
      const address = chain === 'cere' ? addr?.cere : addr?.evm;
      downloadTextFile(json, `${address ?? chain}.${chain}.json`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function revealSolana() {
    setError(null);
    try {
      setSolanaSecret(await identity.revealSolanaSecret());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSolanaConfirming(false);
    }
  }

  const passphraseTooShort = passphrase.length > 0 && passphrase.length < MIN_PASSPHRASE_LENGTH;
  const passphraseMismatch = confirmPassphrase.length > 0 && passphrase !== confirmPassphrase;
  const canDownload =
    passphrase.length >= MIN_PASSPHRASE_LENGTH && confirmPassphrase.length > 0 && passphrase === confirmPassphrase;

  return (
    <Card>
      <h2 style={{ margin: '0 0 var(--space-3)', font: '600 16px/1.2 var(--font-sans)' }}>
        Export / back up your wallet
      </h2>
      <p
        style={{ margin: '0 0 var(--space-4)', font: '400 13px/1.5 var(--font-sans)', color: 'var(--text-secondary)' }}
      >
        These files hold your account keys. Anyone with a file and its password — or the Solana secret — controls this
        account. Store them somewhere safe and never share them.
      </p>

      <Field
        label="Encryption passphrase"
        type="password"
        autoComplete="new-password"
        value={passphrase}
        placeholder="Protects the downloaded keystore files"
        hint={`At least ${MIN_PASSPHRASE_LENGTH} characters`}
        error={passphraseTooShort ? `Must be at least ${MIN_PASSPHRASE_LENGTH} characters` : undefined}
        onChange={(e) => setPassphrase(e.currentTarget.value)}
      />

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Field
          label="Confirm passphrase"
          type="password"
          autoComplete="new-password"
          value={confirmPassphrase}
          placeholder="Re-enter the passphrase"
          error={passphraseMismatch ? 'Passphrases do not match' : undefined}
          onChange={(e) => setConfirmPassphrase(e.currentTarget.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        <Button size="sm" fullWidth={false} disabled={!canDownload || busy !== null} onClick={() => download('cere')}>
          {busy === 'cere' ? 'Exporting…' : 'Download Cere keystore'}
        </Button>
        <Button size="sm" fullWidth={false} disabled={!canDownload || busy !== null} onClick={() => download('evm')}>
          {busy === 'evm' ? 'Exporting…' : 'Download EVM keystore'}
        </Button>
      </div>

      <div
        style={{
          marginTop: 'var(--space-5)',
          borderTop: '1px solid var(--stroke-on-low)',
          paddingTop: 'var(--space-4)',
        }}
      >
        <p
          style={{
            margin: '0 0 var(--space-2)',
            font: '500 13px/1.4 var(--font-sans)',
            color: 'var(--text-secondary)',
          }}
        >
          Solana (Phantom) — raw private key, cannot be encrypted. Reveal only in private.
        </p>
        {solanaSecret ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <CopyValue label="Solana secret key (base58)" value={solanaSecret} truncate={false} />
            <Button size="sm" fullWidth={false} variant="secondary" onClick={() => setSolanaSecret(null)}>
              Clear
            </Button>
          </div>
        ) : solanaConfirming ? (
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button size="sm" fullWidth={false} variant="danger" onClick={revealSolana}>
              Confirm reveal
            </Button>
            <Button size="sm" fullWidth={false} variant="secondary" onClick={() => setSolanaConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" fullWidth={false} variant="secondary" onClick={() => setSolanaConfirming(true)}>
            Reveal Solana secret
          </Button>
        )}
      </div>

      {error && <ErrorText>{error}</ErrorText>}
    </Card>
  );
}
